import {
  useGetReceiptBatchListPhoenix,
  usePliantTransactions,
  getIncompletePliantTransactionsParams,
  useReceiptsPhoenix,
} from '@pflegenavi/frontend/api-nursing-home';
import { IncompleteTransactionsCountContext } from '@pflegenavi/frontend/layout';
import {
  useIsNursingHomeScope,
  useScopeFilter,
  useScopeLegalEntityId,
  useSelectedEmployeeIds,
} from '@pflegenavi/frontend/nursing-home-context';
import { ReceiptStatusValues } from '@pflegenavi/shared/api';
import * as React from 'react';

import type { JSX } from 'react';

export const IncompleteTransactionsProvider = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const legalEntityId = useScopeLegalEntityId();
  const { nursingHomeId } = useScopeFilter();
  // The receipt entries of the menu are nursing-home-bound and hidden at the
  // wider scopes, so counting them there would inflate a badge for pages the
  // user cannot reach.
  const isNursingHomeScope = useIsNursingHomeScope();
  const employeeIds = useSelectedEmployeeIds();

  const { data: receipts, isLoading } = useReceiptsPhoenix(
    {
      nursing_home_ids: nursingHomeId ? [nursingHomeId] : [],
      page: 1,
      page_size: 1,
      receipt_states: [
        ReceiptStatusValues.INCOMPLETE,
        ReceiptStatusValues.DRAFT,
      ],
      created_by_employee_ids: employeeIds,
    },
    {
      staleTime: 0,
      enabled: isNursingHomeScope,
    }
  );

  const { data: receiptBatches, isPending: isLoadingReceiptBatchCount } =
    useGetReceiptBatchListPhoenix(
      {
        params: {
          nursing_home_ids: nursingHomeId ? [nursingHomeId] : [],
          page: 1,
          page_size: 1,
          status: null,
          created_by_employee_ids: employeeIds,
        },
      },
      {
        staleTime: 0,
        enabled: isNursingHomeScope,
      }
    );

  const { data: pliantTransactions, isLoading: isLoadingPliantTransactions } =
    usePliantTransactions(
      getIncompletePliantTransactionsParams(
        1,
        10,
        { legalEntityId, nursingHomeId },
        employeeIds
      )
    );

  const countReceipts =
    !isNursingHomeScope || (isLoading && isLoadingReceiptBatchCount)
      ? 0
      : (receipts?.meta?.total_count ?? 0) +
        (receiptBatches?.meta?.total_count ?? 0);
  const countPliant = isLoadingPliantTransactions
    ? 0
    : pliantTransactions?.meta?.total_count ?? 0;

  return (
    <IncompleteTransactionsCountContext.Provider
      value={{
        all: countReceipts + countPliant,
        receipts: countReceipts,
        pliantTransactions: countPliant,
      }}
    >
      {children}
    </IncompleteTransactionsCountContext.Provider>
  );
};
